import React, { useState } from "react";
import clsx from "clsx";
import styles from "./ComponentCard.module.css";
import ProductSelectionDrawer from "./ProductSelectionDrawer";
import ProductDetailsDrawer from "./ProductDetailsDrawer";
import { useViewModel, useIsB2B } from "../../hooks/useViewModel.ts";
import { selectPrice } from "../../utils/priceHelpers.ts";
import type {
	ProductDto,
	ValidationStatus,
	RuleResult,
} from "../../types/validation";
import { VALIDATION_STATUS } from "../../types/validation";
import {
	Change,
	CheckmarkCircle,
	ExclamationMarkTriangle,
} from "../common/icons";
import { StringSystem } from "../../types/viewmodel.ts";

interface ComponentCardProps {
	kind: string;
	title?: string;
	meta?: string[];
	description?: string;
	imageUrl?: string;
	stockStatus?: {
		iconClass?: string;
		description?: string;
	};
	onReadMoreClick?: () => void;
	onSelectClick?: () => void;
	selectButtonText?: string;
	isInDrawer?: boolean;
	children?: React.ReactNode;
	configBundleId?: string;
	productGroupId?: string;
	materialNumber?: string;
	onProductSelect?: (
		productGroupId: string,
		productId: string | null,
		productData?: any,
	) => void;
	selectedProductId?: string | null;
	defaultProductName?: string | null;
	isCustomized?: boolean;
	isSelected?: boolean;
	forcedDefault?: boolean;
	isEmpty?: boolean;
	canSelectEmpty?: boolean;
	validationStatus?: ValidationStatus;
	validationErrors?: RuleResult[];
	currentConfiguration?: ProductDto[];
	isReplaced?: boolean;
	priceDifference?: {
		net: number;
		gross: number;
	};
	performanceComponentType?: string | null;
}

export interface PerformanceBarProps {
	percentageDifference: number;
	/** The percentage value that maps to the full half-bar. Defaults to 100. */
	maxPercentage?: number;
	label?: string;
	strings: StringSystem;
}

export const PerformanceBar: React.FC<PerformanceBarProps> = ({
	percentageDifference,
	maxPercentage = 100,
	label,
	strings,
}) => {
	const isBetter = percentageDifference > 0;
	const isWorse = percentageDifference < 0;
	const absPercentage = Math.abs(percentageDifference);

	// Fill as a fraction of each half of the bar
	const fillFraction = Math.min(absPercentage / maxPercentage, 1);
	const halfBarPercentage = fillFraction * 50;

	const percentageText =
		absPercentage > 0
			? isBetter
				? strings.product.configBundles.performanceBar.better.replace(
						"{performance}",
						String(Math.round(absPercentage)),
					)
				: strings.product.configBundles.performanceBar.worse.replace(
						"{performance}",
						String(Math.round(absPercentage)),
					)
			: null;

	return (
		<div className={styles.performanceBarContainer}>
			<div className={styles.performanceBarHeader}>
				{percentageText ? (
					<span className={styles.performancePercentLabel}>
						{percentageText}
					</span>
				) : (
					<span className={styles.performanceBarLabel}>{label}</span>
				)}
			</div>
			<div className={styles.performanceBarTrack}>
				<div
					className={clsx(
						styles.performanceBarFill,
						styles.performanceBarWorse,
					)}
					style={{ width: isWorse ? `${halfBarPercentage}%` : "0%" }}
				/>
				<div className={styles.performanceBarCenter} />
				<div
					className={clsx(
						styles.performanceBarFill,
						styles.performanceBarBetter,
					)}
					style={{ width: isBetter ? `${halfBarPercentage}%` : "0%" }}
				/>
			</div>
		</div>
	);
};

interface PriceDifferenceBadgeProps {
	priceDifference: {
		net: number;
		gross: number;
	};
	isB2B?: boolean;
}

export const PriceDifferenceBadge: React.FC<PriceDifferenceBadgeProps> = ({
	priceDifference,
	isB2B = false,
}) => {
	const price = selectPrice(priceDifference, isB2B);
	const isPositive = price > 0;
	const isNegative = price < 0;
	const formattedPrice =
		new Intl.NumberFormat("nb-NO", {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(Math.abs(price)) + ",-";

	const displayText = isPositive
		? `+ ${formattedPrice}`
		: isNegative
			? `- ${formattedPrice}`
			: formattedPrice;

	return <div className={styles.priceDifferenceBadge}>{displayText}</div>;
};

const ComponentCard: React.FC<ComponentCardProps> = ({
	kind,
	title,
	meta,
	description,
	imageUrl,
	stockStatus,
	onReadMoreClick,
	onSelectClick,
	isInDrawer = false,
	children,
	configBundleId,
	productGroupId,
	materialNumber,
	onProductSelect,
	selectedProductId,
	defaultProductName,
	isCustomized = false,
	isSelected = false,
	forcedDefault = false,
	isEmpty = false,
	canSelectEmpty = false,
	validationStatus,
	validationErrors,
	currentConfiguration,
	priceDifference,
	isReplaced = false,
	performanceComponentType = null,
	selectButtonText,
}) => {
	const [isSelectionDrawerOpen, setIsSelectionDrawerOpen] = useState(false);
	const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);

	const handleLesMerClick =
		onReadMoreClick || (() => setIsDetailsDrawerOpen(true));
	const handleSelectClick =
		onSelectClick || (() => setIsSelectionDrawerOpen(true));

	const { data } = useViewModel();
	const { strings } = data!;
	const isB2B = useIsB2B();

	const isEmptyProduct = isEmpty || !title || !materialNumber;

	const displayTitle = isEmptyProduct
		? strings.product.configBundles?.notSelected?.webText1
		: title;
	const displayDescription = isEmptyProduct
		? strings.product.configBundles?.notSelected?.webText2
		: description;
	const displayMeta = isEmptyProduct ? [] : meta || [];

	const getButtonText = () => {
		if (isEmptyProduct) {
			return strings.product.configBundles.notSelected.notChosen;
		}
		if (forcedDefault) {
			return strings.product.configBundles?.defaultSelectedForcedDefault;
		}
		if (isInDrawer && isSelected) {
			return strings.product.configBundles.selected;
		}
		return selectButtonText || strings.product.configBundles.edit;
	};

	const isButtonDisabled = isSelected || forcedDefault;

	const getValidationBadges = () => {
		if (!validationStatus) return null;

		if (!validationErrors || validationErrors.length === 0) {
			return (
				<span className={clsx(styles.validationBadge, styles.success)}>
					<CheckmarkCircle aria-hidden="true" />
					{strings.product.configBundles.compatibility.compatible}
				</span>
			);
		}

		return (
			<>
				{validationErrors.map((rule, i) => {
					const isError = rule.severity === VALIDATION_STATUS.ERROR;
					const isWarning = rule.severity === VALIDATION_STATUS.WARNING;
					const message = (
						rule.shortMessage ||
						strings.product.configBundles.compatibility.notCompatible
					).slice(0, 60);

					return (
						<span
							key={i}
							className={clsx(styles.validationBadge, {
								[styles.error]: isError,
								[styles.warning]: isWarning,
								[styles.success]: !isError && !isWarning,
							})}
						>
							{isError ? (
								<Change aria-hidden="true" />
							) : isWarning ? (
								<ExclamationMarkTriangle aria-hidden="true" />
							) : (
								<CheckmarkCircle aria-hidden="true" />
							)}
							{message}
						</span>
					);
				})}
			</>
		);
	};

	return (
		<>
			<div
				className={clsx(styles.card, {
					[styles.cardSelected]: isSelected && isInDrawer,
				})}
			>
				<div className={clsx(styles.imageContainer, { [styles.imageContainerInDrawer]: isInDrawer })}>
					{isEmptyProduct ? (
						<div className={clsx(styles.thumbnail, styles.emptyThumbnail)}>
							<span className={styles.emptyIcon}>+</span>
						</div>
					) : (
						<img
							src={imageUrl}
							alt={displayTitle ?? ""}
							className={styles.thumbnail}
						/>
					)}
				</div>
				<div className={styles.content}>
					<div className={styles.header}>
						<div className={styles.titleSection}>
							<div className={styles.kind}>
								{isInDrawer ? title : kind}
								{isCustomized && !isEmptyProduct && (
									<span className={styles.customizedBadge}>
										{" "}
										• {strings.product.configBundles.configured}
									</span>
								)}
							</div>
							<h3 className={styles.title}>
								{isInDrawer ? description : displayTitle}
							</h3>
						</div>
						<div className={styles.headerRight}>
							{isInDrawer && priceDifference && (
								<PriceDifferenceBadge priceDifference={priceDifference} isB2B={isB2B} />
							)}
							<div className={styles.metaList}>
								{displayMeta.map((item, index) => (
									<span key={index} className={styles.metaBadge}>
										{item}
									</span>
								))}
							</div>
						</div>
					</div>

					{children}

					<div>
						{stockStatus && (
							<div className={styles.leftFooterSection}>
								<div className={styles.stockStatus}>
									<div
										className={clsx(
											styles.stockIndicator,
											stockStatus.iconClass,
										)}
									/>
									<span className={styles.stockText}>
										{stockStatus.description}
									</span>
								</div>
								{isInDrawer && (
									<span
										className={styles.partNumber}
									>{`${strings.product.configBundles?.partNumber}: ${materialNumber}`}</span>
								)}
							</div>
						)}
					</div>

					<div className={styles.footer}>
						{isReplaced && (
							<span className={clsx(styles.validationBadge, styles.warning)}>
								<ExclamationMarkTriangle aria-hidden="true" />
								{strings.product.configBundles.componentReplaced}
							</span>
						)}
						{!isEmptyProduct ? (
							<>
								{validationStatus && getValidationBadges()}
								<button
									className={styles.linkButton}
									onClick={handleLesMerClick}
									data-testid={
										!productGroupId
											? `component-card-product-${materialNumber}-read-more-button`
											: `component-card-group-${productGroupId}-read-more-button`
									}
								>
									{strings.product.configBundles.readMore}
								</button>
							</>
						) : !isReplaced ? (
							<div className={styles.footerSpacer} />
						) : null}
						<button
							className={styles.selectButton}
							onClick={handleSelectClick}
							disabled={isButtonDisabled}
							data-testid={
								!productGroupId
									? `component-card-product-${materialNumber}-edit-button`
									: `component-card-group-${productGroupId}-edit-button`
							}
						>
							{getButtonText()}
						</button>
					</div>
				</div>
			</div>

			{!isInDrawer && (
				<>
					<ProductSelectionDrawer
						isOpen={isSelectionDrawerOpen}
						onClose={() => setIsSelectionDrawerOpen(false)}
						productKind={kind}
						performanceComponentType={performanceComponentType}
						configBundleId={configBundleId!}
						productGroupId={productGroupId!}
						currentProductId={selectedProductId ?? materialNumber ?? null}
						defaultProductName={defaultProductName}
						onProductSelect={onProductSelect}
						canSelectEmpty={canSelectEmpty}
						currentConfiguration={currentConfiguration}
						strings={strings}
					/>

					{!isEmptyProduct && (
						<ProductDetailsDrawer
							isOpen={isDetailsDrawerOpen}
							onClose={() => setIsDetailsDrawerOpen(false)}
							product={{
								materialNumber: materialNumber,
								kind,
								title: displayTitle!,
								meta: displayMeta,
								description: displayDescription!,
								imageUrl: imageUrl || "",
								stockStatus,
								priceDifference,
								manufacturer: undefined,
							}}
						/>
					)}
				</>
			)}
		</>
	);
};

export default ComponentCard;